import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import './Navbar.css';

const VIEW_NAMES = {
  '/dashboard': 'Tablero',
  '/change-password': 'Cambiar Contraseña',
  '/catalogs': 'Catálogos',
  '/admin': 'Administración de Portal',
  '/admin-users': 'Administración de Usuarios',
  '/admin-logs': 'Registros de Comportamiento',
  '/admin-timelog-permissions': 'Permisos de Bitácora',
  '/requirements': 'Bitácora de Requerimientos',
  '/activities': 'Bitácora de Actividades',
  '/activities/report': 'Bitácora de Actividades',
  '/reports': 'Creación de Reportes',
  '/reports/activities': 'Creación de Reportes',
  '/reports/weekly': 'Creación de Reportes',
  '/requirements-dashboard': 'Dashboard de Requerimientos',
  '/access-denied': 'Acceso Denegado',
};

function IconGrid() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconEnvelope() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function formatLastSignIn(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const tz = 'America/Monterrey';
  const dateParts = date.toLocaleDateString('es-MX', {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeParts = date.toLocaleTimeString('es-MX', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const formattedDate = dateParts
    .replace(/^(\w)/, (c) => c.toUpperCase())
    .replace(/\bde (\w)/g, (_, m) => `de ${m.toUpperCase()}`)
    .replace(/de (20\d{2})/, 'del $1');
  return `${formattedDate}, ${timeParts} hrs`;
}

function ProfileModal({ profile, loading, onClose }) {
  return (
    <>
      <div
        className="profile-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
      >
        <h2 id="profile-title" className="profile-modal-title">
          Tu perfil en sistema
        </h2>
        <div className="profile-modal-body">
          {loading ? (
            <div className="profile-loading">Cargando…</div>
          ) : (
            <>
              <div className="profile-field">
                <span className="profile-label">Nombre Completo:</span>
                <span className="profile-value">{profile?.fullName ?? '—'}</span>
              </div>
              <div className="profile-field">
                <span className="profile-label">Rol en Sistema:</span>
                <span className="profile-value">{profile?.role ?? '—'}</span>
              </div>
              <div className="profile-field">
                <span className="profile-label">Correo WSP:</span>
                <span className="profile-value">{profile?.email ?? '—'}</span>
              </div>
              <div className="profile-field">
                <span className="profile-label">Último Log In:</span>
                <span className="profile-value">{formatLastSignIn(profile?.lastSignIn)}</span>
              </div>
            </>
          )}
        </div>
        <div className="profile-modal-footer">
          <button className="profile-close-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </>
  );
}

function ContactCard({ name, email }) {
  return (
    <div className="help-contact-card">
      <div className="help-contact-info">
        <span className="help-contact-name">{name}</span>
        <span className="help-contact-email">{email}</span>
      </div>
      <div className="help-contact-actions">
        <a
          href={`mailto:${email}`}
          className="help-contact-btn help-contact-btn--email"
          title="Enviar correo en Outlook"
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconEnvelope />
          <span>Outlook</span>
        </a>
        <a
          href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`}
          className="help-contact-btn help-contact-btn--teams"
          title="Abrir chat en Teams"
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconChat />
          <span>Teams</span>
        </a>
      </div>
    </div>
  );
}

function HelpModal({ gerentes, loading, onClose }) {
  return (
    <>
      <div className="profile-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
      >
        <h2 id="help-title" className="profile-modal-title">
          Centro de Ayuda
        </h2>
        <div className="help-modal-body">
          <div className="help-section">
            <h3 className="help-section-title">Problemas con información</h3>
            <p className="help-section-desc">
              Si detectas datos incorrectos en el sistema, contacta a un Gerente:
            </p>
            {loading ? (
              <div className="profile-loading">Cargando…</div>
            ) : gerentes?.length === 0 ? (
              <p className="help-empty">No hay gerentes disponibles en este momento.</p>
            ) : (
              <div className="help-contacts">
                {gerentes?.map((g) => (
                  <ContactCard key={g.email} name={g.full_name} email={g.email} />
                ))}
              </div>
            )}
          </div>
          <div className="help-section">
            <h3 className="help-section-title">Problemas técnicos</h3>
            <p className="help-section-desc">
              Para reportar errores o bugs en el sistema, contacta al desarrollador:
            </p>
            <div className="help-contacts">
              <ContactCard name="Fernando Núñez" email="fernando.nunez@wsp.com.mx" />
            </div>
          </div>
        </div>
        <div className="profile-modal-footer">
          <button className="profile-close-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </>
  );
}

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [gerentes, setGerentes] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);

  const viewName = VIEW_NAMES[location.pathname] ?? '';

  async function openProfile() {
    if (profileOpen) {
      setProfileOpen(false);
      return;
    }
    setHelpOpen(false);
    setProfileOpen(true);
    if (profile) return;
    setProfileLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from('users')
        .select('full_name, roles(name)')
        .eq('id', user.id)
        .single();
      setProfile({
        fullName: data?.full_name,
        role: data?.roles?.name,
        email: user.email,
        lastSignIn: user.last_sign_in_at,
      });
    } finally {
      setProfileLoading(false);
    }
  }

  function closeProfile() {
    setProfileOpen(false);
  }

  async function openHelp() {
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    setProfileOpen(false);
    setHelpOpen(true);
    if (gerentes !== null) return;
    setHelpLoading(true);
    try {
      const { data } = await supabase.rpc('get_gerente_contacts');
      setGerentes(data ?? []);
    } finally {
      setHelpLoading(false);
    }
  }

  function closeHelp() {
    setHelpOpen(false);
  }

  async function handleLogout() {
    setMobileOpen(false);
    setProfileOpen(false);
    setHelpOpen(false);
    await supabase.auth.signOut();
    navigate('/');
  }

  function navTo(path) {
    setMobileOpen(false);
    setProfileOpen(false);
    setHelpOpen(false);
    navigate(path);
  }

  function isActive(path) {
    return location.pathname === path;
  }

  const navItems = [
    {
      label: 'Tablero',
      icon: <IconGrid />,
      onClick: () => navTo('/dashboard'),
      active: isActive('/dashboard'),
    },
    {
      label: 'Perfil',
      icon: <IconUser />,
      onClick: openProfile,
      active: profileOpen,
    },
    {
      label: 'Contraseña',
      icon: <IconLock />,
      onClick: () => navTo('/change-password'),
      active: isActive('/change-password'),
    },
    {
      label: 'Ayuda',
      icon: <IconHelp />,
      onClick: openHelp,
      active: helpOpen,
    },
    {
      label: 'Cerrar Sesión',
      icon: <IconLogout />,
      onClick: handleLogout,
      active: false,
      logout: true,
    },
  ];

  return (
    <>
    <header className="navbar">
      {/* ── Left: logo + divider + view name ── */}
      <div className="navbar-left">
        <img src="/WSP_LOGO-04.png" alt="WSP" className="navbar-logo" />
        <span className="navbar-divider" aria-hidden="true" />
        <span className="navbar-view-name">{viewName}</span>
      </div>

      {/* ── Right: desktop nav ── */}
      <nav className="navbar-right" aria-label="Navegación principal">
        {navItems.map((item) => (
          <button
            key={item.label}
            className={[
              'nav-btn',
              item.active ? 'nav-btn--active' : '',
              item.logout ? 'nav-btn--logout' : '',
            ].join(' ').trim()}
            onClick={item.onClick}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* ── Hamburger (mobile only) ── */}
      <button
        className="navbar-hamburger"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <IconClose /> : <IconMenu />}
      </button>

      {/* ── Mobile dropdown ── */}
      <div
        className={`navbar-mobile-menu${mobileOpen ? ' navbar-mobile-menu--open' : ''}`}
        aria-hidden={!mobileOpen}
      >
        {navItems.map((item) => (
          <button
            key={item.label}
            className={[
              'nav-mobile-btn',
              item.active ? 'nav-btn--active' : '',
              item.logout ? 'nav-btn--logout' : '',
            ].join(' ').trim()}
            onClick={item.onClick}
            tabIndex={mobileOpen ? 0 : -1}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>

    </header>

    {/* ── Profile modal (rendered outside <header> so it isn't trapped
         by the navbar's transform containing block) ── */}
    {profileOpen && (
      <ProfileModal
        profile={profile}
        loading={profileLoading}
        onClose={closeProfile}
      />
    )}

    {/* ── Help modal ── */}
    {helpOpen && (
      <HelpModal
        gerentes={gerentes}
        loading={helpLoading}
        onClose={closeHelp}
      />
    )}
    </>
  );
}

export default Navbar;
